import React, {useState, useCallback} from 'react'
import {SIDEBAR_ITEM_IDS} from '../../../constants/sidebarConfig'
import {useSidebar} from '../../hooks/useSidebar'
import {useAddImageApply} from '../../hooks/useAddImageApply'
import {getDesignLibraryImage} from '../../../../../services/ArtworkTemplateServices'
import {addNode} from '../../../editorActions'
import {EditorConfig} from '../../../constants/editorConfig'
import {toaster} from '../../../../../shared/PFToast'
import NodesPanel from './NodesPanel'
import EditorDevToolsPanel from './EditorDevToolsPanel'
import ShapesPanel from './ShapesPanel'
import AddImageModal from './AddImageModal'
import Icon from '../../../../common/Icon'
import {placeNodeAtViewportCenter} from '../../../utils/placeNodeAtViewportCenter'

const Sidebar = ({runtime, state, dispatch, currentDraftId, onSelectDraft, onSelectShape, onOpenRemoveBg}) => {
    const [showAddImageModal, setShowAddImageModal] = useState(false)

    const {items, activeItem, onItemClick} = useSidebar(runtime, {
        onAddImage: useCallback(() => setShowAddImageModal(true), []),
    })

    // --- Add-image orchestration ---
    const {handlePickFile} = useAddImageApply({runtime, draftId: currentDraftId})

    const handleSelectLibraryImage = useCallback(
        async (imageId) => {
            try {
                const {success, data, message} = await getDesignLibraryImage(imageId)
                if (!success) throw new Error(message)
                const url = data?.origin_url || data?.link
                if (url && runtime?.dispatch) {
                    const id = Date.now().toString()
                    const node = placeNodeAtViewportCenter(
                        {
                            ...EditorConfig.DEFAULT_IMAGE_NODE,
                            id,
                            content: {...EditorConfig.DEFAULT_IMAGE_NODE.content, src: url},
                            style: {...EditorConfig.DEFAULT_IMAGE_NODE.style},
                        },
                        runtime.getViewportCenter?.(),
                    )
                    runtime.dispatch(addNode(node, 0, true))
                }
            } catch (err) {
                toaster({type: 'error', message: err?.message || 'Failed to load image'})
            }
        },
        [runtime],
    )

    return (
        <div className="CreateCustomShape__SidebarWrap">
            <aside className="CreateCustomShape__Toolbar" aria-label="Editor tools">
                <div className="CreateCustomShape__ToolbarSection">
                    {items.map((item) => {
                        const isActive = item.panelId != null && activeItem === item.panelId
                        const ItemIcon = item.icon
                        return (
                            <button
                                key={item.id}
                                type="button"
                                className={`CreateCustomShape__ToolbarItem ${isActive ? 'is-active' : ''}`}
                                onClick={() => onItemClick(item)}
                                aria-label={item.label}
                                aria-pressed={item.panelId != null ? isActive : undefined}
                            >
                                {ItemIcon && (
                                    <span className="CreateCustomShape__ToolbarIcon">
                                        <Icon icon={ItemIcon} />
                                    </span>
                                )}
                                <span className="CreateCustomShape__ToolbarLabel">{item.label}</span>
                            </button>
                        )
                    })}
                </div>
            </aside>

            {activeItem === SIDEBAR_ITEM_IDS.LAYERS && (
                <NodesPanel state={state} dispatch={dispatch} onOpenRemoveBg={onOpenRemoveBg} />
            )}
            {activeItem === SIDEBAR_ITEM_IDS.DEVTOOLS && runtime?.devTools && (
                <EditorDevToolsPanel devTools={runtime.devTools} runtime={runtime} />
            )}
            {activeItem === SIDEBAR_ITEM_IDS.SHAPES && (
                <ShapesPanel runtime={runtime} onSelectDraft={onSelectDraft} onSelectShape={onSelectShape} />
            )}

            <AddImageModal
                isOpen={showAddImageModal}
                toggle={() => setShowAddImageModal((v) => !v)}
                onPickFile={handlePickFile}
                onSelectLibraryImage={handleSelectLibraryImage}
            />
        </div>
    )
}

export default Sidebar
